'use client';

import { ChangeEvent, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ZepApiStatusType } from '@zep/apis';
import { useDialog } from '@zep/dialog';
import { FolderCheckFillIcon, UploadIcon } from '@zep/icons';
import { Button, Input, Select, SelectItemType, TextField } from '@zep/ui';
import { useDeleteApp, useUploadApps, ZepAppType } from '../../../../../../services';
import { isLocal } from '../../../../../../utils';
import { InputImage } from '../../../../../common/InputImage/InputImage';
import { AppSchema, AppSchemaSpec, AppSchemaType } from './AppSchema';
import S from './AppUpload.module.scss';
const REMOVE_ICON_IMAGE_ACTION = 'REMOVE_IMAGE';
export const useAppUploadForm = () => {
  return useFormContext<AppSchemaType>();
};
type InitDefaultValues = Partial<{
  name: string;
  description: string;
  type: number;
  file: string;
  icon: string;
  url?: string;
}>;
const Provider = ({
  children,
  initValues
}: PropsWithChildren<{
  initValues?: InitDefaultValues;
}>) => {
  const form = useForm<AppSchemaType>({
    resolver: zodResolver(AppSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      description: '',
      type: ZepAppType.Normal,
      file: undefined,
      icon: undefined
    }
  });
  useEffect(() => {
    if (initValues) {
      form.setValue('name', initValues.name ?? '');
      form.setValue('description', initValues.description ?? '');

      // @ts-ignore
      form.setValue('type', +initValues.type);
      form.setValue('icon', initValues.icon);

      // @ts-ignore
      form.setValue('file', initValues.file);
      if (isLocal) {
        form.setValue('url', initValues.url);
      }
    }
  }, [initValues, form]);
  return <FormProvider {...form} data-sentry-element="FormProvider" data-sentry-component="Provider" data-sentry-source-file="AppUploadForm.tsx">
      {children}
      {/*{isDevelopment && <DevTool control={form.control} />}*/}
    </FormProvider>;
};
const InputName = () => {
  const {
    t
  } = useTranslation(['accounts']);
  const {
    watch,
    register,
    formState: {
      errors: {
        name: error
      }
    }
  } = useAppUploadForm();
  const errorText = (() => {
    if (!error) return '';
    return t('accounts:myApp.error.required');
  })();
  return <>
      <TextField label={<Label title={t('accounts:myApp.upload.appName')} maxLength={`${watch('name').length || 0}/${AppSchemaSpec.name.maxLength.value}`} />} helperText={errorText} error={!!errorText} data-sentry-element="TextField" data-sentry-source-file="AppUploadForm.tsx">
        <Input isFullWidth maxLength={AppSchemaSpec.name.maxLength.value} placeholder={t('accounts:myApp.upload.appNameDescription')} error={!!errorText} {...register('name')} data-sentry-element="Input" data-sentry-source-file="AppUploadForm.tsx" />
      </TextField>
    </>;
};
const InputDescription = () => {
  const {
    t
  } = useTranslation(['accounts']);
  const {
    watch,
    register,
    formState: {
      errors: {
        description: error
      }
    }
  } = useAppUploadForm();
  const errorText = (() => {
    if (!error) return '';
    return t('accounts:myApp.error.required');
  })();
  return <TextField label={<Label title={t('accounts:myApp.upload.appDescriptionLabel')} maxLength={`${watch('description').length || 0}/${AppSchemaSpec.description.maxLength.value}`} />} helperText={errorText} error={!!errorText} data-sentry-element="TextField" data-sentry-component="InputDescription" data-sentry-source-file="AppUploadForm.tsx">
      <Input {...register('description')} isFullWidth maxLength={AppSchemaSpec.description.maxLength.value} error={!!errorText} placeholder={t('accounts:myApp.upload.appDescription')} data-sentry-element="Input" data-sentry-source-file="AppUploadForm.tsx" />
    </TextField>;
};
const SelectType = () => {
  const {
    t
  } = useTranslation(['accounts']);
  const {
    control
  } = useAppUploadForm();
  const options = [{
    value: ZepAppType.Normal.toString(),
    name: t('accounts:myApp.upload.normalApp')
  }, {
    value: ZepAppType.MiniGame.toString(),
    name: t('accounts:myApp.upload.miniGame')
  }, {
    value: ZepAppType.Sidebar.toString(),
    name: t('accounts:myApp.upload.sidebar')
  }];
  return <Controller control={control} name={'type'} render={({
    field: {
      value,
      onChange
    }
  }) => <TextField label={t('accounts:myApp.upload.appType')}>
          <Select isFullWidth value={options.find(option => +option.value === value)} options={options} size={'md'} onChange={(item: SelectItemType) => onChange(Number(item.value))} />
        </TextField>} data-sentry-element="Controller" data-sentry-component="SelectType" data-sentry-source-file="AppUploadForm.tsx" />;
};
const UploadAppIcon = () => {
  const {
    control,
    clearErrors,
    watch,
    formState: {
      errors: {
        icon: error
      }
    }
  } = useAppUploadForm();
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['accounts']);
  const [previewImage, setPreviewImage] = useState(null);
  const ref = useRef<HTMLInputElement>();
  const icon = watch('icon');
  const errorText = (() => {
    if (!error) return '';
    return t('accounts:myApp.error.required');
  })();
  const handlePreviewImage = (file: File) => {
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }

    // @ts-ignore
    setPreviewImage(URL.createObjectURL(file));
  };
  const isValidateImage = (files: FileList) => {
    if (files.length === 0) return false;
    const file = files[0];
    if (!['jpeg', 'jpg', 'png'].find(ext => ext === file.type.split('/')[1])) {
      dialog.alert({
        title: t('accounts:myApp.error.image')
      });

      // @ts-ignore
      ref.current.value = '';
      return false;
    }
    if (AppSchemaSpec.icon.maxSize.value < file.size) {
      dialog.alert({
        title: t('accounts:myApp.error.imageSize')
      });

      // @ts-ignore
      ref.current.value = '';
      return false;
    }
    return true;
  };
  useEffect(() => {
    if (typeof icon === 'string' && icon !== 'none_image' && icon !== REMOVE_ICON_IMAGE_ACTION) {
      // @ts-ignore
      setPreviewImage(icon);
    }
  }, [icon, setPreviewImage]);
  return <Controller name={'icon'} control={control} render={({
    field: {
      name,
      onChange
    }
  }) => <TextField label={t('accounts:myApp.upload.appIcon')} description={<div className={S.label_text}>
              {t('accounts:myApp.upload.appIconDescription')}
            </div>} error={!!errorText} helperText={errorText}>
          <InputImage
    // @ts-ignore
    ref={ref} name={name} clearImage={() => {
      // @ts-ignore
      ref.current.value = '';

      // @ts-ignore
      setPreviewImage('');
      onChange(REMOVE_ICON_IMAGE_ACTION);
    }} onUploadImage={(_, files) => {
      // @ts-ignore
      if (isValidateImage(files)) {
        // @ts-ignore
        handlePreviewImage(files[0]);

        // @ts-ignore
        onChange(files[0]);
        clearErrors('icon');
      }
    }}
    // @ts-ignore
    imageSrc={previewImage} acceptType={['jpeg', 'jpg', 'png']} />
        </TextField>} data-sentry-element="Controller" data-sentry-component="UploadAppIcon" data-sentry-source-file="AppUploadForm.tsx" />;
};
const UploadFile = () => {
  const {
    control,
    clearErrors,
    watch,
    formState: {
      errors: {
        file: error
      }
    }
  } = useAppUploadForm();
  const isFile = !!watch('file');
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['accounts']);
  const fileRef = useRef<HTMLInputElement>();
  const errorText = (() => {
    if (!error) return '';
    return t('accounts:myApp.error.required');
  })();
  const accept = ['.zip', '.rar', '.7zip'];
  const isValidateFile = (e: ChangeEvent<HTMLInputElement>) => {
    const result = false;
    const zipTypes = ['application/x-zip', 'application/zip', 'application/x-zip-compressed', 'application/octet-stream'];

    // @ts-ignore
    if (e.currentTarget.files.length === 0) return result;

    // @ts-ignore
    const file = e.currentTarget.files[0];
    if (!zipTypes.find(f => f === file.type)) {
      dialog.alert({
        title: t('accounts:myApp.error.onlyZip')
      });

      // @ts-ignore
      fileRef.current.value = null;
      return result;
    }
    if (file.size > AppSchemaSpec.file.maxSize.value) {
      dialog.alert({
        title: t('accounts:myApp.error.maxFileSize')
      });

      // @ts-ignore
      fileRef.current.value = null;
      return result;
    }
    return true;
  };
  return <Controller name={'file'} control={control} render={({
    field: {
      value,
      onChange
    }
  }) => {
    return <TextField label={t('accounts:myApp.upload.zepScriptFile')} error={!!errorText} description={<>
                <span className={S.label_text}>
                  {t('accounts:myApp.upload.zepScriptFileDescription')}
                </span>
                {value && <div className={S.upload_file_complete_label}>
                    <FolderCheckFillIcon />
                    {t('accounts:myApp.upload.completeFileUpload')}
                  </div>}
              </>} helperText={errorText}>
            <div>
              <input type="file" hidden
        // @ts-ignore
        ref={fileRef} onChange={e => {
          if (isValidateFile(e)) {
            // @ts-ignore
            onChange(e.currentTarget.files[0]);
            clearErrors('file');
          }
        }} accept={accept.join(',')} />
              <Button className="min-w-[120px]" size={'xs'} variant={'sub'} leftSlot={<UploadIcon width={14} height={14} />}
        // @ts-ignore
        onClick={() => fileRef.current.click()}>
                {isFile ? t('accounts:myApp.upload.updateAppFile') : t('accounts:myApp.upload.uploadZipFile')}
              </Button>
            </div>
          </TextField>;
  }} data-sentry-element="Controller" data-sentry-component="UploadFile" data-sentry-source-file="AppUploadForm.tsx"></Controller>;
};
export const SubmitButton = ({
  title: _title,
  appHashId,
  isUpdated = false
}: {
  title: string;
  appHashId?: string;
  isUpdated?: boolean;
}) => {
  const router = useRouter();
  const {
    handleSubmit,
    getValues,
    watch,
    setError,
    setValue,
    formState: {
      errors,
      isDirty
    }
  } = useAppUploadForm();
  const dialog = useDialog();
  const disabled = Object.keys(errors).length > 0 || !isDirty || !isUpdated && !watch('file');
  const {
    t
  } = useTranslation(['common', 'accounts']);
  const {
    mutateAsync,
    isLoading
  } = useUploadApps();
  const trimString = (dataValues: {
    name: string;
    description: string;
  }) => {
    let isError = false;
    for (const dataValuesKey in dataValues) {
      // @ts-ignore
      if (String(dataValues[dataValuesKey]).trim() === '') {
        setError(dataValuesKey as 'name' | 'description', {
          type: 'required'
        }, {
          shouldFocus: true
        });
        setValue(dataValuesKey as 'name' | 'description', '');
        isError = true;
      } else {
        setValue(dataValuesKey as 'name' | 'description',
        // @ts-ignore
        dataValues[dataValuesKey].trim());
      }
    }
    return isError;
  };
  const onSubmit = () => {
    if (isLoading) return;
    const {
      type,
      file,
      name,
      description,
      icon,
      url
    } = getValues();
    const isError = trimString({
      name,
      description
    });
    if (isError) return;
    mutateAsync({
      type,
      description,
      name,
      file: file as File,
      // @ts-ignore
      icon: icon instanceof File ? icon : undefined,
      isUpdated,
      appHashId,
      isDeletedIcon: icon === REMOVE_ICON_IMAGE_ACTION,
      url
    }).then(() => {
      router.push('/me/apps');
    }).catch(error => {
      let title: string = t('common:error.api.unknown');
      let content = undefined;
      if (error.code === 'ECONNABORTED') title = t('accounts:myApp.error.timeout');
      if (error.status === ZepApiStatusType.NOT_VALID_ZIP_FILE) title = t('accounts:myApp.error.onlyZip');
      if (error?.status === ZepApiStatusType.NOT_FOUND_ENTRY_SCRIPT_FILE) {
        title = t('accounts:myApp.error.notFundEntryFile');
        content = t('accounts:myApp.error.notFundEntryFileContent');
      }
      if (error?.status === ZepApiStatusType.SIZE_LIMIT_EXCEEDED) title = t('accounts:myApp.error.maxjsFileSize');
      dialog.alert({
        title,
        content
      });
      console.error(error);
    });
  };
  return <Button className="w-[120px]" onClick={handleSubmit(onSubmit)} isLoading={isLoading} disabled={disabled || isLoading} data-sentry-element="Button" data-sentry-component="SubmitButton" data-sentry-source-file="AppUploadForm.tsx">
      {_title}
    </Button>;
};
const InputLocalZepFilePath = () => {
  const {
    register
  } = useAppUploadForm();
  if (!isLocal) return null;
  return <TextField label={'ZepScript Path'} description={'해당 입력창은 개발 환경에서만 작동합니다.'} data-sentry-element="TextField" data-sentry-component="InputLocalZepFilePath" data-sentry-source-file="AppUploadForm.tsx">
      <Input isFullWidth placeholder={'scripts/ProjectName'} {...register('url')} data-sentry-element="Input" data-sentry-source-file="AppUploadForm.tsx" />
    </TextField>;
};
const DeleteButton = ({
  appHashId,
  appName
}: {
  appHashId: string;
  appName: string;
}) => {
  const router = useRouter();
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['accounts']);
  const {
    mutateAsync
  } = useDeleteApp();
  const handleDelete = async () => {
    const confirm = await dialog.confirm({
      title: t('accounts:myApp.update.deleteConfirmTitle', {
        appName
      }),
      confirmText: t('accounts:myApp.update.deleteConfirmApply'),
      confirmVariant: 'alert'
    });
    if (!confirm) return;
    mutateAsync(appHashId).catch(e => {
      console.error(e);
    }).then(() => {
      router.push('/me/apps');
    });
  };
  return <Button variant="alert" className="w-[120px]" onClick={handleDelete} data-sentry-element="Button" data-sentry-component="DeleteButton" data-sentry-source-file="AppUploadForm.tsx">
      {t('accounts:myApp.update.deleteConfirmApply')}
    </Button>;
};
const Label = (props: {
  title: string;
  maxLength: string;
}) => <div className={S.label} data-sentry-component="Label" data-sentry-source-file="AppUploadForm.tsx">
    <span>{props.title}</span>
    <span className={S.label_length}>{props.maxLength}</span>
  </div>;
export const AppUploadForm = {
  Provider,
  InputName,
  InputDescription,
  SelectType,
  InputLocalZepFilePath,
  UploadAppIcon,
  UploadFile,
  SubmitButton,
  DeleteButton
};